
import { Button } from "@/components/ui/button";
import { Check, Lock, ChevronRight } from "lucide-react";
import { PricingFeatureSection } from "./PricingFeatureSection";
import { cn } from "@/lib/utils";

interface PricingPlanCardProps {
  title: string;
  description: string;
  price: string;
  period: string;
  savings?: string;
  sessionInfo: string;
  features: Array<{
    title: string;
    isLocked?: boolean;
    features: string[];
  }>;
  variant?: 'default' | 'pro';
  expandedFeatures: string[];
  onFeatureToggle: (section: string) => void;
  onGetStarted: () => void;
  className?: string;
  popular?: boolean;
}

export function PricingPlanCard({
  title,
  description,
  price,
  period,
  savings,
  sessionInfo,
  features,
  variant = 'default',
  expandedFeatures,
  onFeatureToggle,
  onGetStarted,
  className,
  popular
}: PricingPlanCardProps) {
  const isPro = variant === 'pro';
  
  return (
    <div className={cn(
      "rounded-2xl p-8 border relative",
      isPro ? "bg-[#111827] border-gray-800" : "bg-white border-gray-200",
      className
    )}>
      {popular && (
        <div className="absolute -top-3 right-8">
          <span className="bg-[#818CF8] text-white px-3 py-1 rounded-full text-sm font-medium">
            Popular
          </span>
        </div>
      )}

      <h3 className={cn("text-xl font-semibold", isPro ? "text-white" : "text-gray-900")}>{title}</h3>
      <p className={cn("mt-2", isPro ? "text-gray-400" : "text-gray-500")}>{description}</p>

      <div className="mt-6">
        <div className="flex items-baseline">
          <span className={cn("text-3xl font-bold", isPro ? "text-white" : "")}>{price}</span>
          <span className={cn("ml-2", isPro ? "text-gray-400" : "text-gray-500")}>/{period}</span>
          {savings && (
            <span className={cn(
              "ml-2 text-sm font-medium",
              isPro ? "text-[#818CF8]" : "text-green-600"
            )}>
              {savings}
            </span>
          )}
        </div>
        <div className="mt-2">
          <span className={cn(
            "inline-block rounded-full px-3 py-1 text-sm font-medium",
            isPro ? "bg-[#818CF8]/10 text-[#818CF8]" : "bg-blue-50 text-blue-600"
          )}>
            {sessionInfo}
          </span>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {features.map((feature, index) => (
          <PricingFeatureSection
            key={index}
            title={feature.title}
            icon={feature.isLocked ? 
              <Lock className="h-3.5 w-3.5 text-gray-400" /> : 
              <Check className={cn("h-4 w-4", isPro ? "text-[#818CF8]" : "text-blue-600")} />
            }
            features={feature.features}
            isLocked={feature.isLocked}
            isExpanded={expandedFeatures.includes(feature.title)}
            onToggle={() => onFeatureToggle(feature.title)}
            variant={variant}
          />
        ))}
      </div>

      <Button 
        className={cn(
          "w-full mt-8",
          isPro ? "bg-[#818CF8] text-white hover:bg-[#818CF8]/90" : "bg-[#111827] text-white hover:bg-gray-800"
        )}
        onClick={onGetStarted}
      >
        {isPro ? (
          <>
            Start Free Trial
            <ChevronRight className="h-4 w-4 ml-1" />
          </>
        ) : (
          "Get Started"
        )}
      </Button>
    </div>
  );
}
